import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Image, Space, Spin, Steps, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';

import LanguageSwitcher from '../components/LanguageSwitcher';
import './OrderTracking.scss';

const STATUS_COLORS = {
  DRAFT: 'default',
  UNASSIGNED: 'default',
  PENDING: 'gold',
  ASSIGNED: 'blue',
  OPTIMIZED: 'cyan',
  DISPATCHED: 'geekblue',
  PICKUP_ARRIVED: 'purple',
  COLLECTED: 'purple',
  DELIVERY_ARRIVED: 'purple',
  COMPLETED: 'green',
  SKIPPED: 'orange',
  DELIVERY_FAILED: 'red',
  PICKUP_FAILED: 'red',
  ERROR: 'red',
  SERVICE_ARRIVED: 'purple',
};

const TRACKING_STEP_KEYS = [
  { key: 'DISPATCHED', titleKey: 'tracking.dispatched', icon: <SendOutlined /> },
  { key: 'DELIVERY_ARRIVED', titleKey: 'tracking.arrived', icon: <EnvironmentOutlined /> },
  { key: 'COMPLETED', titleKey: 'tracking.completed', icon: <CheckCircleOutlined /> },
];

const FAILED_STATUSES = ['DELIVERY_FAILED', 'PICKUP_FAILED', 'SKIPPED', 'ERROR'];

const OrderTracking = () => {
  const { t, i18n } = useTranslation('orders');
  const { orderCode, token } = useParams();

  // Default to French for public tracking page
  useEffect(() => {
    if (!localStorage.getItem('i18nextLng')) {
      i18n.changeLanguage('fr');
    }
  }, []);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URI}/orders/track/${orderCode}/${token}`);
        setOrder(response.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderCode, token]);

  const getArrivalTime = () => {
    if (!order?.logs) return null;
    const arrivalLog = order.logs.find((log) => log.status === 'DELIVERY_ARRIVED' || log.status === 'SERVICE_ARRIVED');
    return arrivalLog ? moment(arrivalLog.createdAt) : null;
  };

  const getCompletedTime = () => {
    if (!order?.logs) return null;
    const completedLog = order.logs.find((log) => log.status === 'COMPLETED');
    return completedLog ? moment(completedLog.createdAt) : null;
  };

  const getDispatchedTime = () => {
    if (!order?.logs) return null;
    const dispatchedLog = order.logs.find((log) => log.status === 'DISPATCHED');
    return dispatchedLog ? moment(dispatchedLog.createdAt) : null;
  };

  const getCurrentStep = () => {
    if (!order) return -1;
    if (FAILED_STATUSES.includes(order.status)) return -1;
    if (order.status === 'COMPLETED') return 3;
    if (order.status === 'DELIVERY_ARRIVED' || order.status === 'SERVICE_ARRIVED') return 2;
    if (['DISPATCHED', 'COLLECTED', 'PICKUP_ARRIVED'].includes(order.status)) return 1;
    return 0;
  };

  const isFailed = order && FAILED_STATUSES.includes(order.status);

  if (loading) {
    return (
      <div className="OrderTracking">
        <div className="tracking-container">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="OrderTracking">
        <div className="tracking-container">
          <div className="tracking-header">
            <h1>OVO Planner</h1>
            <LanguageSwitcher compact />
          </div>
          <div className="tracking-error">
            <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
            <h2>{t('tracking.orderNotFound')}</h2>
            <p>{t('tracking.invalidLink')}</p>
          </div>
        </div>
      </div>
    );
  }

  const arrivalTime = getArrivalTime();
  const completedTime = getCompletedTime();
  const dispatchedTime = getDispatchedTime();
  const currentStep = getCurrentStep();
  const podFiles = order.fileOrders?.map((fo) => fo.file).filter(Boolean) || [];

  return (
    <div className="OrderTracking">
      <div className="tracking-container">
        <div className="tracking-header">
          <h1>OVO Planner</h1>
          <LanguageSwitcher compact />
          {order.partnerName && (
            <p className="tracking-subtitle">{t('tracking.deliveringFrom', { partner: order.partnerName })}</p>
          )}
          <div className="order-code">{order.partnerOrderRef || order.orderCode}</div>
        </div>

        <div className="tracking-status">
          <Tag color={STATUS_COLORS[order.status] || 'default'} className="status-tag">
            {(order.status || '').replace(/_/g, ' ')}
          </Tag>
        </div>

        {!isFailed && (
          <div className="tracking-timeline">
            <Steps
              current={currentStep}
              items={TRACKING_STEP_KEYS.map((step) => ({
                title: t(step.titleKey),
                icon: step.icon,
              }))}
            />
          </div>
        )}

        {isFailed && (
          <div className="tracking-failed">
            <CloseCircleOutlined style={{ fontSize: 36, color: '#ff4d4f' }} />
            <p>{order.status === 'SKIPPED' ? t('tracking.deliverySkipped') : t('tracking.deliveryFailed')}</p>
          </div>
        )}

        <div className="tracking-details">
          <div className="detail-section">
            <h3>{t('tracking.deliveryDetails')}</h3>
            {order.description && (
              <div className="detail-row">
                <label>{t('tracking.description')}</label>
                <span>{order.description}</span>
              </div>
            )}
            {order.weight && (
              <div className="detail-row">
                <label>{t('tracking.weight')}</label>
                <span>{order.weight} kg</span>
              </div>
            )}
            <div className="detail-row">
              <label>{t('tracking.address')}</label>
              <span>{order.deliveryAddress || '-'}</span>
            </div>
            {order.deliveryDate && (
              <div className="detail-row">
                <label>{t('tracking.date')}</label>
                <span>{moment(order.deliveryDate).format('DD/MM/YYYY')}</span>
              </div>
            )}
            {(order.deliveryTimeStart || order.deliveryTimeEnd) && (
              <div className="detail-row">
                <label>{t('tracking.timeWindow')}</label>
                <span>
                  {order.deliveryTimeStart || ''} - {order.deliveryTimeEnd || ''}
                </span>
              </div>
            )}
            {dispatchedTime && (
              <div className="detail-row">
                <label>{t('tracking.dispatched')}</label>
                <span>{dispatchedTime.format('DD/MM/YYYY HH:mm')}</span>
              </div>
            )}
            {arrivalTime && (
              <div className="detail-row">
                <label>{t('tracking.arrival')}</label>
                <span>{arrivalTime.format('DD/MM/YYYY HH:mm')}</span>
              </div>
            )}
            {completedTime && (
              <div className="detail-row">
                <label>{t('tracking.completed')}</label>
                <span>{completedTime.format('DD/MM/YYYY HH:mm')}</span>
              </div>
            )}
          </div>

          {podFiles.length > 0 && (
            <div className="detail-section">
              <h3>{t('tracking.proofOfDelivery')}</h3>
              <div className="pod-gallery">
                <Image.PreviewGroup>
                  <Space size={8} wrap>
                    {podFiles.map((file) => (
                      <Image
                        key={file.id}
                        src={order.host + '/' + file.key}
                        alt="Proof of Delivery"
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: 8 }}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            </div>
          )}
        </div>

        <div className="tracking-footer">
          <p>{t('tracking.poweredBy')}</p>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
